import { clsx } from 'clsx';
import { useContext } from 'react';
import { ConnectedFacility, SourceTypeEnum } from '../../../js/api';
import { SessionContext } from '../../../js/app/contexts/SessionContext';
import { useCurrencyFormatter } from '../../../js/app/hooks/use-currency-formatter';
import { SOURCE_TYPE_ORGANIZATION_BADGE_STYLE } from '../utils/dataStyles';

type ConnectionRateInformationProps = {
  connection: ConnectedFacility;
};

/**
 * The "Hourly Rates" box of a connection: the hourly rates the viewer's side sees
 * (facility rates for organizations and staff, shift rates for the clinician) for
 * daytime / overnight / 24 hour / call, plus the 1099 badge on an organization-sourced
 * connection. One box wherever a connection's rates are shown (Details tab,
 * Presentation Info tab).
 */
export function ConnectionRateInformation({ connection }: ConnectionRateInformationProps) {
  const { audienceIsProvider } = useContext(SessionContext);
  const isProvider = audienceIsProvider ?? false;
  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '-' });

  // Get rates based on user type
  const dayRate = isProvider ? connection.shift_rate_day : connection.facility_rate_day;
  const nightRate = isProvider ? connection.shift_rate_night : connection.facility_rate_night;
  const rate24h = isProvider ? connection.shift_rate_24h : connection.facility_rate_24h;
  const callRate = isProvider ? connection.shift_rate_call : connection.facility_rate_call;

  return (
    <div
      className="tw:bg-brand-green-3 tw:rounded-md tw:mt-3 tw:p-2"
      data-testid="connection-rate-information"
    >
      <div className="tw:text-sm tw:font-medium tw:mb-1 tw:flex tw:justify-between tw:items-center">
        Hourly Rates
        {connection.source_type === SourceTypeEnum.ORGANIZATION && (
          <span
            className={clsx(
              'tw:flex tw:items-center tw:gap-1',
              SOURCE_TYPE_ORGANIZATION_BADGE_STYLE
            )}
          >
            1099
          </span>
        )}
      </div>
      <div className="tw:grid tw:grid-cols-4 tw:gap-1">
        {!!dayRate && dayRate !== 0 && (
          <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
            {formatCurrencyPostfix(dayRate)}
            <div className="tw:text-xs tw:italic tw:text-zinc-500">Daytime</div>
          </div>
        )}
        {!!nightRate && nightRate !== 0 && (
          <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
            {formatCurrencyPostfix(nightRate)}
            <div className="tw:text-xs tw:italic tw:text-zinc-500">Overnight</div>
          </div>
        )}
        {!!rate24h && rate24h !== 0 && (
          <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
            {formatCurrencyPostfix(rate24h)}
            <div className="tw:text-xs tw:italic tw:text-zinc-500">24 hour</div>
          </div>
        )}
        {!!callRate && callRate !== 0 && (
          <div className="tw:text-brand-green-0 tw:font-medium tw:gap-1 tw:mb-1">
            {formatCurrencyPostfix(callRate)}
            <div className="tw:text-xs tw:italic tw:text-zinc-500">Call</div>
          </div>
        )}
      </div>
    </div>
  );
}
